import type { ProjectSummary } from "../api/projects";

type ProjectLike = Pick<ProjectSummary, "display_name" | "project_key"> &
  Partial<Pick<ProjectSummary, "project_code" | "project_code_raw">>;

export type ProjectDisplay = {
  title: string;
  meta: string | null;
  tooltip: string;
};

const PREFIX_RE = /^(code|ref|address):\s*(.+)$/i;
const ACRONYMS = new Set(["AG", "AT", "BHKW", "GMBH", "KG", "OG", "OWG", "SV"]);

function normalizeWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function splitPrefixed(value: string): { kind: string | null; body: string } {
  const match = normalizeWhitespace(value).match(PREFIX_RE);
  if (!match) {
    return { kind: null, body: normalizeWhitespace(value) };
  }
  return { kind: match[1].toLowerCase(), body: normalizeWhitespace(match[2]) };
}

function titleCaseAllCaps(value: string): string {
  return value
    .split(" ")
    .map((word) => {
      if (!word || word !== word.toUpperCase()) return word;
      if (ACRONYMS.has(word)) return word;
      if (/^\d/.test(word)) return word;
      return word.charAt(0) + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function formatAddress(value: string): string {
  const spaced = value
    .replace(/([A-Za-z])(?=\d{1,4}\b)/g, "$1 ")
    .replace(/\b(\d{4})\s+(.+)$/g, "$1 $2");
  const titled = titleCaseAllCaps(normalizeWhitespace(spaced));
  return titled.replace(/\s+(\d{4})\s+([^,]+)$/g, ", $1 $2");
}

export function formatProjectValue(value: string | null | undefined): string | null {
  if (!value) return null;
  const { kind, body } = splitPrefixed(value);
  if (!body) return null;
  if (kind === "code") return `Projekt ${body}`;
  if (kind === "address") return formatAddress(body);
  return titleCaseAllCaps(body);
}

export function projectCodeFromKey(value: string | null | undefined): string | null {
  if (!value) return null;
  const { kind, body } = splitPrefixed(value);
  return kind === "code" && body ? body : null;
}

export function projectDisplay(
  project: ProjectLike | undefined,
  projectKey: string | null | undefined,
): ProjectDisplay | null {
  const key = projectKey ?? project?.project_key ?? null;
  const title =
    formatProjectValue(project?.display_name) ??
    formatProjectValue(project?.project_code_raw) ??
    formatProjectValue(project?.project_code) ??
    formatProjectValue(key);

  if (!title) return null;

  const code =
    project?.project_code ??
    project?.project_code_raw ??
    projectCodeFromKey(key);
  const normalizedCode = code ? splitPrefixed(code).body : null;
  const meta =
    normalizedCode && title !== `Projekt ${normalizedCode}`
      ? `Nr. ${normalizedCode}`
      : null;
  const tooltipParts = [
    title,
    meta,
    project?.display_name && project.display_name !== title
      ? `Original: ${project.display_name}`
      : null,
    key ? `Key: ${key}` : null,
  ].filter(Boolean);

  return {
    title,
    meta,
    tooltip: tooltipParts.join("\n"),
  };
}

export function projectSearchText(
  project: ProjectLike | undefined,
  projectKey: string | null | undefined,
): string {
  const display = projectDisplay(project, projectKey);
  return [
    display?.title,
    display?.meta,
    project?.display_name,
    project?.project_code,
    project?.project_code_raw,
    projectKey,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}
